import {
  useEffect,
  type Dispatch,
  type SetStateAction
} from 'react';
import FiltersComponent from '../../common/components/filters/FiltersComponent';
import NoCrawlData from '../components/common/NoCrawlData';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import PageDrawer from '../components/pages/PageDrawer';
import BubbleChart from '../components/internal-link/BubbleChart';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { InternalLinkColumns } from '../config/columnsConfig';
import { Card, Col, Row } from 'react-bootstrap';
import {
  useGetCrawlInternalLinksMutation,
  useCountCrawlInternalLinksQuery
} from '@/app/Crawl/api/internalLinksApi';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetCountGroupQuery } from '@/app/Crawl/api/anchortTextApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useExportTableMutation } from '@/app/Crawl/api/exportApi';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import useDomain from '@/hooks/useDomain';

type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type LinksData = {
  job_page?: string;
  total?: number;
  rows?: unknown[];
};

type LinksResponse = {
  data?: LinksData;
};

const InternalLink = () => {
  const [, setBreadcrumbData] =
    useOutletContext<CrawlOutletContext>();
  const { domain_slug } = useDomain();
  const access = useToolAccess('crawl');
  const { slug } = useParams();
  useGetUserInfoQuery();
  const job_page = slug === 'last' ? domain_slug : slug;
  const [getAnchorText] = useGetCrawlInternalLinksMutation();
  const [exportTable] = useExportTableMutation();
  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useCountCrawlInternalLinksQuery(
    { job_page, domain_slug },
    { skip: access.isLoading || !access.haveAccess || !job_page || !domain_slug }
  );
  const data = rawData as LinksResponse | undefined;
  const linksData = data?.data;

  useEffect(() => {
    setBreadcrumbData([{ text: 'پیوند داخلی', href: '#' }]);
  }, []);

  const { data: chartData, isLoading: isChartLoading } = useGetCountGroupQuery(
    {
      job_page,
      domain_slug,
      group_by: 'link_text',
      link_type: 'internal',
      limit: 100
    },
    { skip: access.isLoading || !access.haveAccess || !job_page || !domain_slug }
  );

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار خزش" />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoading && !linksData) return <NoCrawlData />;

  return (
    <Row>
      <Col xxl={12}>
        {!isChartLoading && chartData ? (
          <BubbleChart data={chartData} />
        ) : (
          <BubbleChartSkeleton />
        )}
      </Col>
      <Col xxl={12}>
        <FiltersComponent columns={InternalLinkColumns} />
      </Col>
      <Col xxl={12}>
        {!isLoading ? (
          <AgGridWrapper
            table_name={location.pathname.split('/').slice(1, 3).join('/')}
            fetchFunction={getAnchorText}
            height="746px"
            columns={InternalLinkColumns}
            additionalData={{
              job_page: linksData?.job_page,
              domain_slug
            }}
            settings={{
              pagination: true,
              export: {
                enabled: true,
                fetchFunction: exportTable
              }
            }}
             
            total={linksData?.total}
            data={linksData?.rows}
          />
        ) : (
          <AgGridSkeleton height="700px" />
        )}
        <PageDrawer />
      </Col>
    </Row>
  );
};

export default InternalLink;
// اسکلت لودینگ برای چارت
const BubbleChartSkeleton = () => (
  <Card className="my-2">
    <FalconCardHeader
      title={
        <div>
          متن پیوند <HelpTooltip html="hello world 213123" />
        </div>
      }
      titleTag="h6"
      className="py-2"
      light
    />
    <Card.Body className="py-2 my-2 d-flex flex-center position-relative">
      <div className="position-relative w-100" style={{ height: '40rem' }}>
        <div className="d3-tooltip"></div>
      </div>
    </Card.Body>
  </Card>
);
